import { formatClockTime } from '@/shared/lib/time-format';
import { useFormContext } from 'react-hook-form';
import { cn } from '@/lib/utils';
import {
  ArrowRight,
  Building2,
  Calendar,
  Home,
  MapPin,
  Pencil,
  Video,
  X,
} from 'lucide-react';
import type { ComponentType, SVGProps } from 'react';
import { useAvailableSlots } from '@/services/scheduling/use-available-slots';
import { NO_SLOT_AVAILABILITY_MESSAGE } from '@/scheduling/lib/appointment-strings';
import { formatLocalDatetimeForWire } from '@/shared/lib/date-format';
import type { AppointmentFormValues } from '@/types/appointment-form';
import { formatSlotDate } from './slot-date';

interface TimeSlotsSectionProps {
  practiceId: string;
  /** Open the full Time & Date sub-page (Edit pencil on the selected card). */
  onOpenTimeDate?: () => void;
}


/** Slot time in the current format ("13:45", or "1:45p" under the 12-hour preference). */
function fmt12h(iso: string) {
  return formatClockTime(new Date(iso));
}

const fmtDate = formatSlotDate;

/**
 * TimeSlotsSection — slot picker for the selected provider+profile+date.
 * Mirrors Flutter `date_time_widget.dart` slot [2]:
 *   - When `appointment.start == null` → show the first 1–2 slot chips
 *     (we render the full grid here for usability)
 *   - When a slot is picked → show a single "selected slot" card with
 *     date + time and Edit / Delete actions
 */
export function TimeSlotsSection({ practiceId, onOpenTimeDate }: TimeSlotsSectionProps) {
  const { watch, setValue } = useFormContext<AppointmentFormValues>();
  const providerId = watch('providerId');
  const selectedDate = watch('selectedDate');
  const selectedSlotId = watch('slotId');
  const slotStart = watch('slotStart');
  const appointmentProfileId = watch('appointmentProfileId');
  const location = watch('location');
  const LocationIcon = locationIcon(location?.name ?? '');

  // Slot window ships LOCAL-CLOCK, no `Z` (PROD-1778 rule, see
  // shared/lib/date-format.ts). `toISOString()` converts to UTC first, so in
  // Dubai (GMT+4) local midnight became the PREVIOUS day at 20:00 and the
  // backend — which reads the string literally — answered with the wrong
  // window: measured 2026-08-24 asking as `2026-08-23T20:00:00.000Z` returned
  // Aug 23 20:00 → Aug 24 19:30. That put four PREVIOUS-day chips at the top of
  // the list and hid the tail of the selected day, so a chip could belong to a
  // different day than the strip claimed.
  const { data: rawSlots = [], isLoading } = useAvailableSlots(
    providerId && practiceId && selectedDate && appointmentProfileId
      ? {
          practiceId,
          practitionerId: providerId,
          appointmentProfileId,
          start: formatLocalDatetimeForWire(
            new Date(new Date(selectedDate).setHours(0, 0, 0, 0)),
          ),
          end: formatLocalDatetimeForWire(
            new Date(new Date(selectedDate).setHours(23, 59, 59, 0)),
          ),
          count: 100,
        }
      : null,
  );

  // Drop slots whose start time has already passed — when the user
  // picks today, anything earlier than `now` isn't bookable. Future
  // dates pass through unchanged because Date.parse(s.start) > now.
  const nowMs = Date.now();
  const slots = rawSlots.filter((s) => s.start && Date.parse(s.start) >= nowMs);

  /**
   * Anchor the two visible chips to the time the user actually ASKED for,
   * rather than always the day's first two.
   *
   * Why this matters beyond tidiness: a receptionist who says or types
   * "tomorrow 10am" was being shown 8:00 and 8:15 and had to open the full
   * sub-page to find the time they had already named. That single detail is
   * the largest reason the Scheduling-AI path costs more taps than booking
   * by hand — the parsed time was carried all the way here and then ignored.
   * It hurts the manual path too, whenever the user picks a date+time and
   * then lands back on the day's opening slots.
   *
   * `selectedDate` only expresses a WISH TIME when it carries one. The date
   * strip writes local midnight (date-picker-section), which is a date with
   * no time — that yields no anchor and behaviour is byte-for-byte unchanged.
   */
  const anchorMs = (() => {
    if (!selectedDate) return null;
    const d = new Date(selectedDate);
    if (Number.isNaN(d.getTime())) return null;
    // Local midnight = "a day", not "a time". No anchor.
    if (d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0) {
      return null;
    }
    return d.getTime();
  })();

  /**
   * First slot at/after the wish, plus the next one. A wish later than the
   * last bookable slot falls back to the day's LAST two (the nearest real
   * options), never to an empty list. No anchor → the day's first two,
   * exactly as before.
   */
  const visibleSlots = (() => {
    if (anchorMs == null) return slots.slice(0, 2);
    const i = slots.findIndex((s) => Date.parse(s.start ?? '') >= anchorMs);
    if (i < 0) return slots.slice(-2);
    return slots.slice(i, i + 2);
  })();

  /**
   * The red × on the selected-slot card. CLEARS ONLY — it must not navigate.
   *
   * Flutter parity, and the two controls are deliberately different:
   *   ×      `create_appointment_widget.dart:1312-1345` (`onUnselect`) refetches
   *          slots for `appointment.selectedDate`; `GetSlotsRespState` (:2176)
   *          then nulls start/end. You stay on the card, which falls through to
   *          its unselected row — two slot chips plus the round forward arrow
   *          (`date_time_widget.dart:119-195`).
   *   arrow  fires `CreateAppointmentStartEvent(dateTimeSelection)` — THIS is
   *          Flutter's only route from the card to the full Time & Date list.
   *   pencil `date_time_widget.dart:87-90`, same event, slot kept.
   *
   * A previous revision had × jump straight to the list. Reverted 2026-08-21 on
   * review: it collapsed × and the arrow into one control, and Back could not
   * undo the clear. Don't re-add it without changing Flutter too.
   */
  const clearSlot = () => {
    setValue('slotId', null, { shouldValidate: true });
    setValue('slotStart', null);
    setValue('slotEnd', null);
  };

  return (
    <div className="px-4 py-3 border-t border-border-subtle">
      <p className="text-xs font-medium text-text-muted mb-2">
        Date &amp; time
      </p>

      {selectedSlotId && slotStart ? (
        // Selected slot card — Flutter date_time_widget.dart:52–117
        <div className="flex items-center gap-3 p-3 rounded-lg border border-brand-primary bg-blue-100">
          <Calendar className="h-5 w-5 text-brand-primary flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-text-muted">
              {fmtDate(slotStart)}
            </p>
            <p className="text-md font-bold text-text-primary leading-tight">
              {fmt12h(slotStart)}
            </p>
          </div>
          {onOpenTimeDate ? (
            <button
              type="button"
              onClick={onOpenTimeDate}
              className="p-2 hover:bg-blue-100 rounded"
              aria-label="Edit time"
            >
              <Pencil className="h-4 w-4 text-brand-primary" />
            </button>
          ) : null}
          <button
            type="button"
            onClick={clearSlot}
            className="p-2 hover:bg-blue-100 rounded"
            aria-label="Clear slot"
          >
            <X className="h-4 w-4 text-status-error" />
          </button>
        </div>
      ) : isLoading ? (
        <div className="flex items-center gap-2">
          <div className="h-[88px] flex-1 rounded-lg bg-grey-200 animate-pulse" />
          <div className="h-[88px] flex-1 rounded-lg bg-grey-200 animate-pulse" />
          <div className="h-8 w-8 rounded-full bg-grey-200 animate-pulse flex-shrink-0" />
        </div>
      ) : slots.length === 0 ? (
        // Even when the inline picker has nothing to show, keep the
        // right-arrow so the user can still open the full Time & Date
        // sub-page and try a different date / location.
        <div className="flex items-center gap-2">
          <p className="flex-1 text-sm text-text-muted py-2 leading-snug">
            {NO_SLOT_AVAILABILITY_MESSAGE}
          </p>
          {onOpenTimeDate ? (
            <button
              type="button"
              onClick={onOpenTimeDate}
              className="h-8 w-8 rounded-full bg-surface-card border border-border-default flex items-center justify-center text-brand-primary hover:border-brand-primary hover:bg-blue-100 flex-shrink-0 shadow-sm"
              aria-label="See all slots"
            >
              <ArrowRight className="h-4 w-4" />
            </button>
          ) : null}
        </div>
      ) : (
        // Flutter parity (date_time_widget.dart:119-196): show the first two
        // slots as date/time/location cards plus a round arrow button that
        // opens the full Time & Date sub-page.
        <div className="flex items-center gap-2">
          {visibleSlots.map((slot, i) => {
            const slotKey = slot.start ?? `slot-${i}`;
            const isSelected = selectedSlotId === slotKey;
            return (
              <button
                key={slotKey}
                type="button"
                onClick={() => {
                  if (!slot.start) return;
                  setValue('slotId', slotKey, { shouldValidate: true });
                  setValue('slotStart', slot.start);
                  setValue('slotEnd', slot.end);
                }}
                className={cn(
                  'flex-1 min-w-0 h-[88px] rounded-lg border bg-surface-card flex flex-col items-center justify-center gap-1 px-2 transition-colors',
                  isSelected
                    ? 'border-brand-primary bg-blue-100'
                    : 'border-border-default hover:border-brand-primary hover:bg-blue-100',
                )}
              >
                <span className="text-xs font-medium text-text-muted">
                  {slot.start ? fmtDate(slot.start) : '—'}
                </span>
                <span className="text-lg font-bold text-text-primary leading-none">
                  {slot.start ? fmt12h(slot.start) : '—'}
                </span>
                <span className="flex items-center gap-1 text-xs text-brand-primary">
                  <LocationIcon className="h-3 w-3" />
                  <span className="truncate">{location?.name ?? ''}</span>
                </span>
              </button>
            );
          })}
          {onOpenTimeDate ? (
            <button
              type="button"
              onClick={onOpenTimeDate}
              className="h-8 w-8 rounded-full bg-surface-card border border-border-default flex items-center justify-center text-brand-primary hover:border-brand-primary hover:bg-blue-100 flex-shrink-0 shadow-sm"
              aria-label="See all slots"
            >
              <ArrowRight className="h-4 w-4" />
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}

function locationIcon(name: string): ComponentType<SVGProps<SVGSVGElement>> {
  switch (name) {
    case 'Office':
      return Building2;
    case 'Home':
      return Home;
    case 'Virtual':
      return Video;
    default:
      return MapPin;
  }
}
